import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Divider,
  Chip
} from '@mui/material';
import { formatCurrency, formatNumber } from './formatters';

interface ProductData {
  descr_compl?: string;
  cod_item?: string;
  total_debito_icms?: number;
  total_credito_icms?: number;
  quantidade_entrada?: number | string;
  quantidade_saida?: number | string;
  quantidade_total?: number | string;
  total_transacoes?: number;
  valor_total_item?: number;
  valor_total_item_compra?: number;
  valor_total_item_venda?: number;
}

interface DrillDownProductInfoProps {
  produto: ProductData;
}

const DrillDownProductInfo: React.FC<DrillDownProductInfoProps> = ({ produto }) => {
  return (
    <Grid item xs={12}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              {produto.descr_compl || 'Produto não encontrado'}
            </Typography>

              {produto.cod_item && produto.cod_item.includes(',') ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Códigos dos Produtos:
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 0.75,
                    width: '100%',
                    alignItems: 'flex-start'
                  }}>
                    {produto.cod_item.split(',')
                    .filter(item => item.trim() !== '')
                    .map((item, index) => (
                      <Chip 
                        key={index}
                        label={item.trim()}
                        color="default"
                        size="small"
                        variant="filled"
                        sx={{ 
                          fontWeight: 500,
                          flexShrink: 0,
                          maxWidth: '100%'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Código do Produto:
                  </Typography>
                  <Chip 
                    label={produto.cod_item || 'N/A'}
                    color="default"
                    size="small"
                    variant="filled"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              )}
          </Box>
        </Box>
        
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Primeira linha - Valores financeiros */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              💰 Valores ICMS
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label={`Créditos: ${formatCurrency(produto.total_credito_icms || 0)}`}
                color="success"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
              />
              <Chip 
                label={`Débitos: ${formatCurrency(produto.total_debito_icms || 0)}`}
                color="error"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
              />
              <Chip 
                label={`Saldo: ${formatCurrency((produto.total_debito_icms || 0) - (produto.total_credito_icms || 0)) }`}
                color={(produto.total_debito_icms || 0) - (produto.total_credito_icms || 0) >= 0 ? 'warning' : 'info'}
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Saldo líquido (Débitos - Créditos)"
              />
            </Box>
          </Grid>

          {/* Segunda linha - Quantidades */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              📦 Movimentação de Produtos
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label={`Entradas: ${formatNumber(Number(produto.quantidade_entrada) || 0)} un.`}
                color="success"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Quantidade de produtos comprados"
              />
              <Chip 
                label={`Saídas: ${formatNumber(Number(produto.quantidade_saida) || 0)} un.`}
                color="warning"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Quantidade de produtos vendidos ou saídos do estoque"
              />
              <Chip 
                label={`Total: ${formatNumber(Number(produto.quantidade_entrada || 0) - Number(produto.quantidade_saida || 0))} un.`}
                color={(Number(produto.quantidade_entrada || 0) - Number(produto.quantidade_saida || 0) >= 0)  ? 'info' :  'warning'}
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Quantidade total movimentada (entradas - saídas) no período"
              />
            </Box>
          </Grid>

          {/* Terceira linha - Valores Financeiros */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              💰 Valores Financeiros
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label={`Compras: ${formatCurrency(produto.valor_total_item_compra || 0)}`}
                color="success"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Valor total dos itens comprados"
              />
              <Chip 
                label={`Vendas: ${formatCurrency(produto.valor_total_item_venda || 0)}`}
                color="warning"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Valor total dos itens vendidos"
              />
              <Chip
                label={`Valor Total: ${formatCurrency((produto.valor_total_item_venda || 0) - (produto.valor_total_item_compra || 0))}`}
                color={(Number(produto.valor_total_item_venda || 0) - Number(produto.valor_total_item_compra || 0) >= 0) ? 'info' : 'warning'}
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Valor total dos itens movimentados (vendas - compras)"
              />
            </Box>
          </Grid>

          {/* Quarta linha - Estatísticas */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              📊 Estatísticas Gerais
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label={`Ticket Médio Compras: ${formatCurrency((Number(produto.valor_total_item_compra) || 0) / (Number(produto.quantidade_entrada) || 1))}`}
                color="secondary"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Valor médio de compra"
              />
              <Chip 
                label={`Ticket Médio Vendas: ${formatCurrency((Number(produto.valor_total_item_venda) || 0) / (Number(produto.quantidade_saida) || 1))}`}
                color="secondary"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Valor médio de compra"
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ mb: 3 }} />
    </Grid>
  );
};

export default DrillDownProductInfo; 